import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { ASSUMPTION_LABEL, pct } from '../../lib/feasibilityLabels';
import type { Assumption, AssumptionChange } from '../../types/feasibility';

/**
 * FZ4 - the defaults behind every number, his and Robert's to change; each change recorded server-side.
 * ⛔ A cost that is not set is NAMED - the builder's value cannot be worked out without it, and nothing fills it in.
 */

const COST_KEYS = Object.keys(ASSUMPTION_LABEL);

/** A share is TYPED as a percent (45) and STORED as a fraction (0.45). */
export const toEntered = (unit: string, value: number): number => (unit === 'fraction' ? Math.round(value * 10_000) / 100 : value);
/**
 * FZ-FIX I8 (the 142nd): the number typed - or null. It did Number(typed), and Number('') is 0: a cleared box went out
 * as a real 0, which the server's own check cannot tell from a typed zero. A typed 0 still reads as 0.
 */
export const fromEntered = (unit: string, typed: string): number | null => {
  const t = typed.trim();
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(t)) return null;
  return unit === 'fraction' ? Number(t) / 100 : Number(t);
};

const shown = (a: Assumption): string => {
  switch (a.unit) {
    case 'fraction': return pct(a.value);
    // To the cent, keeping a third place a source prints ($3.675 a foot in Ontario's fee schedule).
    case 'usd_per_sf': return `$${a.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} per sq ft`;
    case 'sf': return `${a.value} sq ft`;
    case 'miles': return `${a.value} miles`;
    case 'acres': return `${a.value} ac`;
    default: return String(a.value);
  }
};

export default function AssumptionsPanel() {
  const [rows, setRows] = useState<Assumption[]>([]);
  const [history, setHistory] = useState<AssumptionChange[]>([]);
  const [editing, setEditing] = useState<Assumption | null>(null);
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.getFeasibilityAssumptions();
      setRows(r.assumptions);
      setHistory(r.history);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the defaults');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    setError('');
    const value = fromEntered(editing.unit, typed);
    if (value === null) { setError('Type a number - a blank box is not zero, and nothing was changed'); return; }
    try {
      await api.updateFeasibilityAssumption(editing.key, value, note.trim() || null);
      setEditing(null);
      setNote('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the default');
    }
  };

  const missing = COST_KEYS.filter((k) => !rows.some((r) => r.key === k));
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm" aria-label="The defaults">
      <h2 className="font-semibold text-gray-900">The defaults behind every number</h2>
      {error && <p role="alert" className="mt-2 rounded bg-red-50 p-2 text-red-800">{error}</p>}
      <ul className="mt-2 divide-y">
        {rows.map((a) => (
          <li key={a.key} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
            <span>{a.label}</span>
            <span className="font-medium">{shown(a)}</span>
            {/* FZ-FIX F11: a long source address wraps inside the card (the 141st's look: it ran past the edge). */}
            <span className="w-full min-w-0 text-xs text-gray-500 [overflow-wrap:anywhere]">{`${a.source} (as of ${a.as_of})`}</span>
            {/* The caveat he chose the number with travels with it (the 141st's look: stored, shown nowhere). */}
            {a.note && <span className="w-full text-xs text-amber-800">{a.note}</span>}
            <button type="button" className="text-xs text-blue-700 underline" aria-label={`Change ${a.label}`}
              onClick={() => { setEditing(a); setTyped(String(toEntered(a.unit, a.value))); }}>Change</button>
          </li>
        ))}
      </ul>
      {missing.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-amber-800">
          {missing.map((k) => <li key={k}>{`Not set: ${ASSUMPTION_LABEL[k]} - the builder's value cannot be worked out until it has a researched figure.`}</li>)}
        </ul>
      )}
      {editing && (
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded border p-2">
          <label className="flex flex-col">{editing.unit === 'fraction' ? 'New value (%)' : 'New value'}
            <input aria-label={editing.unit === 'fraction' ? 'New value (%)' : 'New value'} className="w-28 rounded border px-2 py-1" inputMode="decimal" value={typed} onChange={(e) => setTyped(e.target.value)} />
          </label>
          <label className="flex flex-1 flex-col">Why (optional)
            <input className="rounded border px-2 py-1" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-white" onClick={() => void save()}>Save</button>
          <button type="button" className="rounded border px-3 py-1.5" onClick={() => setEditing(null)}>Cancel</button>
        </div>
      )}
      {history.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-gray-600">{`Recent changes (${history.length})`}</summary>
          <ul className="mt-1 text-xs text-gray-600">
            {history.map((h) => <li key={`${h.key}-${h.changed_at}`}>{`${h.key}: ${h.old_value ?? '-'} -> ${h.new_value} on ${h.changed_at.slice(0, 10)}${h.note ? ` - ${h.note}` : ''}`}</li>)}
          </ul>
        </details>
      )}
    </section>
  );
}
